import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Supplier } from "@reina/shared";
import {
  Button,
  Dropzone,
  EmptyState,
  Field,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  SupplierAvatar,
  TableWrap,
  useToast,
} from "../../components";
import { useRateCards, useSuppliers } from "../../api/catalogue";
import { usePastePriceList, usePriceLists, useUploadPriceList } from "../../api/priceLists";
import { errorMessage, formatDate, formatInt } from "../../lib/format";
import { priceListStatusLabel, priceListStatusTone } from "../../lib/tones";

export function PriceListsPage() {
  const navigate = useNavigate();
  const priceLists = usePriceLists();
  const suppliers = useSuppliers();
  const items = priceLists.data?.items ?? [];

  const supplierById = useMemo(
    () => new Map((suppliers.data?.items ?? []).map((s) => [s.id, s])),
    [suppliers.data]
  );

  const [modal, setModal] = useState<"upload" | "paste" | null>(null);

  const actionButtons = (
    <>
      <Button variant="ghost" onClick={() => setModal("paste")}>
        Paste
      </Button>
      <Button variant="primary" onClick={() => setModal("upload")}>
        Upload
      </Button>
    </>
  );

  return (
    <>
      <Panel>
        <PanelHead
          title="Price lists"
          sub={
            priceLists.data
              ? `${priceLists.data.total} versioned ingest${priceLists.data.total === 1 ? "" : "s"}`
              : "versioned supplier ingests → SCD2 prices"
          }
          actions={actionButtons}
        />
        {priceLists.isPending ? (
          <PanelBody>
            <EmptyState title="Loading price lists…" />
          </PanelBody>
        ) : priceLists.isError ? (
          <PanelBody>
            <EmptyState title="Could not load price lists" hint={errorMessage(priceLists.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No price lists yet"
              hint="Upload a supplier file (xlsx/csv/pdf) or paste rows to start the ingest history."
              action={actionButtons}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Version</th>
                <th>Source</th>
                <th>Status</th>
                <th>Rows</th>
                <th>File</th>
                <th>Effective</th>
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {items.map((list) => {
                const supplier = supplierById.get(list.supplierId);
                return (
                  <tr
                    key={list.id}
                    onClick={() => navigate(`/price-lists/${list.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      {supplier ? (
                        <span className="result-cell">
                          <SupplierAvatar name={supplier.name} logo={supplier.logo} />
                          <strong>{supplier.name}</strong>
                        </span>
                      ) : (
                        <span className="mono">{list.supplierId.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="mono">v{list.version}</td>
                    <td className="mono">{list.sourceType}</td>
                    <td>
                      <Pill tone={priceListStatusTone[list.status]}>
                        {priceListStatusLabel[list.status]}
                      </Pill>
                    </td>
                    <td className="mono">{formatInt(list.rowCount)}</td>
                    <td className="mono">{list.fileName ?? "—"}</td>
                    <td className="mono">{formatDate(list.effectiveDate)}</td>
                    <td className="mono">{formatDate(list.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      {modal === "upload" && (
        <UploadModal suppliers={suppliers.data?.items ?? []} onClose={() => setModal(null)} />
      )}
      {modal === "paste" && (
        <PasteModal suppliers={suppliers.data?.items ?? []} onClose={() => setModal(null)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Upload modal (multipart: file + supplier + optional effective date)
// ---------------------------------------------------------------------------

function UploadModal({ suppliers, onClose }: { suppliers: Supplier[]; onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const upload = useUploadPriceList();
  const [supplierId, setSupplierId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const submit = async () => {
    if (!supplierId || !file) return;
    try {
      const { priceList } = await upload.mutateAsync({
        file,
        supplierId,
        ...(effectiveDate ? { effectiveDate } : {}),
      });
      toast.push({ message: "Upload received — parsing…", tone: "ok" });
      onClose();
      navigate(`/price-lists/${priceList.id}`);
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <Modal
      open
      title="Upload price list"
      sub="xlsx / csv / pdf — parsed in the background, publish when staged"
      onClose={onClose}
      footer={
        <>
          <span className="sub">Re-uploading for a supplier creates the next version.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={!supplierId || !file || upload.isPending}
            >
              {upload.isPending ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </>
      }
    >
      <FieldGrid>
        <Field label="Supplier">
          <select
            className="field"
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
            autoFocus
          >
            <option value="">Choose…</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Effective date (optional)" hint="Date the prices take effect.">
          <input
            className="field"
            type="date"
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
          />
        </Field>
      </FieldGrid>
      <Dropzone
        accept=".xlsx,.xlsm,.xlsb,.xls,.ods,.csv,.txt,.pdf,.docx,.odt,.png,.jpg,.jpeg,.webp,.eml,.msg"
        state={file ? "ready" : "idle"}
        onFiles={(files) => setFile(files[0] ?? null)}
      >
        {file ? (
          <span className="file-chip">{file.name}</span>
        ) : (
          <>
            <strong>Drag the price file here</strong>
            <span className="sub">.xlsx, .csv or .pdf — or click to browse</span>
          </>
        )}
      </Dropzone>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Paste modal (text + supplier + optional rate card / effective date)
// ---------------------------------------------------------------------------

function PasteModal({ suppliers, onClose }: { suppliers: Supplier[]; onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const paste = usePastePriceList();
  const rateCards = useRateCards();
  const [supplierId, setSupplierId] = useState("");
  const [rateCardId, setRateCardId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [text, setText] = useState("");

  const supplierRateCards = (rateCards.data?.items ?? []).filter(
    (rateCard) => !supplierId || rateCard.supplierId === supplierId
  );

  const submit = async () => {
    if (!supplierId || !text.trim()) return;
    try {
      const { priceList } = await paste.mutateAsync({
        supplierId,
        text,
        ...(rateCardId ? { rateCardId } : {}),
        ...(effectiveDate ? { effectiveDate } : {}),
      });
      toast.push({ message: "Paste received — parsing…", tone: "ok" });
      onClose();
      navigate(`/price-lists/${priceList.id}`);
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <Modal
      open
      title="Paste price rows"
      sub="tab/comma-separated rows — messy pastes fall back to AI extraction"
      onClose={onClose}
      footer={
        <>
          <span className="sub">One product per line; headers are detected automatically.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={!supplierId || !text.trim() || paste.isPending}
            >
              {paste.isPending ? "Submitting…" : "Ingest paste"}
            </Button>
          </div>
        </>
      }
    >
      <FieldGrid columns={3}>
        <Field label="Supplier">
          <select
            className="field"
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
            autoFocus
          >
            <option value="">Choose…</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Rate card (optional)">
          <select
            className="field"
            value={rateCardId}
            onChange={(event) => setRateCardId(event.target.value)}
          >
            <option value="">Not assigned</option>
            {supplierRateCards.map((rateCard) => (
              <option key={rateCard.id} value={rateCard.id}>
                {rateCard.termsCode} — {rateCard.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Effective date (optional)">
          <input
            className="field"
            type="date"
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
          />
        </Field>
      </FieldGrid>
      <Field label="Rows" hint="Paste straight from the supplier email or spreadsheet.">
        <textarea
          className="field"
          rows={10}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={"IQ123456\t15mm Elbow Hep2O\t£1.23\nIQ123457\t22mm Coupler Hep2O\t£0.98"}
        />
      </Field>
    </Modal>
  );
}
